import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, Switch, Modal, TouchableWithoutFeedback, Image } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import EditIcon from '../../../assets/icon-menu.svg';
import OverflowIcon from '../../../assets/icon-overflow.svg';
import PropTypes from 'prop-types';
import { colors, avatarColors } from '../../style/rulesEngine';

const RuleCard = ({ rule, isActive, onEdit, onToggle, onDelete, onDuplicate, onMoveUp, onMoveDown }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 16 });
  const [showDisabledTip, setShowDisabledTip] = useState(false);
  const menuButtonRef = useRef(null);

  useEffect(() => {
    if (!showMenu) return;
    const close = () => setShowMenu(false);
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [showMenu]);

  const openMenu = () => {
    setShowMenu(true);
    menuButtonRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setMenuPos({
        top: pageY + height + 4,
        right: typeof window !== 'undefined' ? window.innerWidth - (pageX + width) : 16,
      });
    });
  };

  const avatarColor = avatarColors[(rule.priority - 1) % avatarColors.length];
  const condCount = rule.conditions_json?.length || 0;
  const actCount = rule.actions_json?.length || 0;
  const subtitle = `${condCount} condition${condCount !== 1 ? 's' : ''} · ${actCount} action${actCount !== 1 ? 's' : ''}`;

  return (
    <View
      style={{
        backgroundColor: '#0d1e30',
        borderRadius: 10,
        marginVertical: 4,
        borderWidth: 1,
        borderColor: isActive ? colors.accent : '#1a3452',
        paddingVertical: 10,
        paddingHorizontal: 12,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        opacity: rule.enabled ? 1 : 0.6,
      }}
    >
      {/* Avatar circle */}
      <View style={{
        width: 38,
        height: 38,
        borderRadius: 19,
        backgroundColor: avatarColor,
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
      }}>
        <Text style={{ fontFamily: 'Poppins_700Bold', color: '#fff', fontSize: 15 }}>
          {rule.priority}
        </Text>
      </View>

      {/* Main content: two rows */}
      <View style={{ flex: 1 }}>
        {/* Row 1: name + right controls */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 5 }}>
          <Text
            style={{ flex: 1, fontFamily: 'Poppins_600SemiBold', color: '#fff', fontSize: 14 }}
            numberOfLines={1}
          >
            {rule.name}
          </Text>

          {/* Priority + status badge */}
          <View style={{
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: 'rgba(51, 101, 153, 0.45)',
            borderRadius: 10,
            paddingHorizontal: 7,
            paddingVertical: 2,
            gap: 3,
          }}>
            <Text style={{ fontFamily: 'Poppins_700Bold', color: '#fff', fontSize: 11 }}>
              {rule.priority}
            </Text>
            <Text style={{
              fontFamily: 'Poppins_600SemiBold',
              color: rule.enabled ? colors.accent : colors.textMuted,
              fontSize: 11,
            }}>
              {rule.enabled ? 'ON' : 'OFF'}
            </Text>
          </View>

          {/* Menu button — opens Edit / Duplicate / Delete dropdown */}
          <TouchableOpacity ref={menuButtonRef} onPress={openMenu} style={{ padding: 3 }}>
            <Image source={EditIcon} style={{ width: 20, height: 20 }} />
          </TouchableOpacity>

        </View>

        {/* Row 2: subtitle + arrows + toggle */}
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text
            style={{ flex: 1, fontFamily: 'Poppins_500Medium', color: colors.textMuted, fontSize: 12 }}
            numberOfLines={1}
          >
            {subtitle}
          </Text>

          {/* Always-visible Up/Down arrow buttons (tablet fallback per §8.7) */}
          <TouchableOpacity
            onPress={onMoveUp || undefined}
            disabled={!onMoveUp}
            style={{ padding: 3, opacity: onMoveUp ? 1 : 0.25 }}
          >
            <MaterialIcons name="arrow-upward" size={16} color={colors.textMuted} />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={onMoveDown || undefined}
            disabled={!onMoveDown}
            style={{ padding: 3, marginRight: 6, opacity: onMoveDown ? 1 : 0.25 }}
          >
            <MaterialIcons name="arrow-downward" size={16} color={colors.textMuted} />
          </TouchableOpacity>

          {/* Toggle with disabled tooltip */}
          <View>
            <TouchableOpacity
              activeOpacity={1}
              onLongPress={() => { if (!rule.enabled) setShowDisabledTip(true); }}
              onPress={() => { if (showDisabledTip) setShowDisabledTip(false); }}
            >
              <Switch
                value={rule.enabled}
                onValueChange={(val) => { setShowDisabledTip(false); onToggle(rule.id, val); }}
                trackColor={{ false: colors.toggleOff, true: colors.toggleOn }}
                thumbColor={colors.text}
              />
            </TouchableOpacity>
            {showDisabledTip && (
              <View style={{
                position: 'absolute', bottom: -28, right: 0,
                backgroundColor: '#0d1e30', borderWidth: 1, borderColor: '#3d5a73',
                borderRadius: 4, paddingHorizontal: 8, paddingVertical: 4, zIndex: 100,
                minWidth: 200,
              }}>
                <Text style={{ fontFamily: 'Poppins_500Medium', fontSize: 11, color: colors.textMuted }}>
                  This rule is paused and won't run
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>

      {/* Overflow icon — card level, vertically centered */}
      <Image source={OverflowIcon} style={{ width: 15, height: 50 }} resizeMode="contain" />

      {/* Overflow menu modal */}
      <Modal
        visible={showMenu}
        transparent
        animationType="none"
        onRequestClose={() => setShowMenu(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowMenu(false)}>
          <View style={{ flex: 1 }}>
            <View style={{
              position: 'absolute',
              top: menuPos.top,
              right: menuPos.right,
              backgroundColor: '#1e3048',
              borderRadius: 8,
              borderWidth: 1,
              borderColor: 'rgba(255,255,255,0.25)',
              zIndex: 9999,
              minWidth: 150,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.4,
              shadowRadius: 8,
              elevation: 20,
            }}>
              <TouchableOpacity
                style={{ paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.1)' }}
                onPress={() => { setShowMenu(false); onEdit(rule); }}
              >
                <Text style={{ fontFamily: 'Poppins_500Medium', fontSize: 14, color: '#fff' }}>
                  Edit
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{ paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.1)' }}
                onPress={() => { setShowMenu(false); onDuplicate(rule.id); }}
              >
                <Text style={{ fontFamily: 'Poppins_500Medium', fontSize: 14, color: '#fff' }}>
                  Duplicate
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{ paddingVertical: 12, paddingHorizontal: 16 }}
                onPress={() => { setShowMenu(false); onDelete(rule); }}
              >
                <Text style={{ fontFamily: 'Poppins_500Medium', fontSize: 14, color: colors.danger }}>
                  Delete
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

RuleCard.propTypes = {
  rule: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    priority: PropTypes.number.isRequired,
    enabled: PropTypes.bool.isRequired,
    conditions_json: PropTypes.array,
    actions_json: PropTypes.array,
  }).isRequired,
  isActive: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
};

export default RuleCard;
